import React from "react";

import Img from "webapp/ui/smart/img/img";
import { LearningCenterStyle } from "./learning-center-style";
import { GlideIcon, StrokeIcons } from "sharedUI/ui/views/glide-icon/glide-icon";
import classNames from "classnames";
import { GlideFC } from "lib/glide-fc";
import { VerticallyExpandableContainer } from "designer/ui/smart/vertically-expandable-container/vertically-expandable-container";

interface VideoLearningItem {
    readonly kind: "video";
    readonly name: string;
    readonly youtubeEmbedID: string;
    readonly playlistID?: string;
}

type LearningItem = VideoLearningItem;

export interface Section {
    readonly name: string;
    readonly data: string | readonly LearningItem[];
    readonly icon?: StrokeIcons;
}

interface Props {
    sections: Section[];
    onClose?: () => void;
}

export const LearningCenterImpl: GlideFC<Props> = p => {
    const { sections, onClose } = p;
    const [sectionRaw, setSectionIndex] = React.useState(0);
    const [openSectionItem, setOpenSectionItem] = React.useState(0);

    const sectionIndex = Math.min(sectionRaw, sections.length - 1);

    const activeSection = sections[sectionIndex] ?? sections[0];

    const onSectionClick = (index: number) => {
        if (index === sectionIndex) return;
        setSectionIndex(index);
        setOpenSectionItem(0);
    };

    if (activeSection === undefined) return null;

    return (
        <LearningCenterStyle>
            <div className="header-bar">
                <div>Learn to Glide</div>
                <button onClick={onClose}>
                    <GlideIcon kind="stroke" icon="st-close" className="close" iconSize={24} />
                </button>
            </div>
            <ul className="menu-bar">
                {sections.map((s, i) => {
                    const inner = (
                        <div className="item-content">
                            <div>{s.name}</div>
                            {s.icon !== undefined && (
                                <GlideIcon kind="stroke" className="section-icon" iconSize={12} icon={s.icon} />
                            )}
                        </div>
                    );
                    const className = classNames(activeSection === s && "selected");
                    if (typeof s.data === "string") {
                        return (
                            <li key={i} className={classNames(className, "pale")}>
                                <a href={s.data} target="_blank" rel="noreferrer">
                                    {inner}
                                </a>
                            </li>
                        );
                    }
                    return (
                        <li key={i} className={className} onClick={() => onSectionClick(i)}>
                            {inner}
                        </li>
                    );
                })}
            </ul>
            {typeof activeSection.data === "string" ? undefined : (
                <div className="section-content">
                    {activeSection.data.map((i, index) => {
                        let href = `https://www.youtube.com/watch?v=${i.youtubeEmbedID}`;
                        if (i.playlistID !== undefined) {
                            href = `${href}&list=${i.playlistID}`;
                        }
                        return (
                            <div key={index} className="section-item">
                                <div className="header" onClick={() => setOpenSectionItem(index)}>
                                    <div>{i.name}</div>
                                    <GlideIcon
                                        kind="stroke"
                                        icon="st-caret"
                                        iconSize={20}
                                        rotateDeg={index === openSectionItem ? 0 : -90}
                                    />
                                </div>
                                <VerticallyExpandableContainer isOpen={index === openSectionItem}>
                                    <div className="section-item-body">
                                        <div className="video-wrapper">
                                            <a target={"_blank"} rel="noreferrer" href={href}>
                                                <div className="aspect-wrapper">
                                                    <Img
                                                        className="video-thumbnail"
                                                        src={`https://img.youtube.com/vi/${i.youtubeEmbedID}/0.jpg`}
                                                    />
                                                </div>
                                            </a>
                                            <GlideIcon
                                                className="play-overlay"
                                                kind="twotone"
                                                icon="tt-large-play"
                                                primaryColor="black"
                                                iconSize={80}
                                                accentColor="white"
                                            />
                                        </div>
                                    </div>
                                </VerticallyExpandableContainer>
                            </div>
                        );
                    })}
                </div>
            )}
        </LearningCenterStyle>
    );
};
